import { Box, StackProps, styled } from '@mui/material';
import {
    CheckmarkIcon,
    CheckmarkOutlinedIcon,
    CloseIcon,
    ColorOptions,
    ExclamationMarkIcon,
    ExclamationMarkOutlinedIcon,
} from '@synergy/theme-next';
import { DeliveryStatus as DeliveryStatusEnum, StockStatus } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { ReactElement } from 'react';
import { shouldForwardPropExclude } from '@synergy/utils';

export type DeliveryStatusVariant = 'contained' | 'circleSmall' | 'circle';

const getDeliveryStatusDisplayValues = (
    deliveryStatus: DeliveryStatusEnum,
    variant: DeliveryStatusVariant,
    stockStatus?: StockStatus
): [ColorOptions, string, ReactElement] => {
    const status = stockStatus === StockStatus.SoldOut ? DeliveryStatusEnum.Unavailable : deliveryStatus;

    switch (status) {
        case DeliveryStatusEnum.Available:
            return [
                'success',
                'app.deliveryStatus.available',
                variant === 'contained' ? (
                    <CheckmarkOutlinedIcon fontSize="inherit" strokeWidth={2} />
                ) : (
                    <CheckmarkIcon fontSize="inherit" strokeWidth={2.5} />
                ),
            ];
        case DeliveryStatusEnum.Tracked:
            return [
                'warning',
                'app.deliveryStatus.tracked',
                variant === 'contained' ? (
                    <ExclamationMarkOutlinedIcon fontSize="inherit" strokeWidth={2} />
                ) : (
                    <ExclamationMarkIcon fontSize="inherit" strokeWidth={2.5} />
                ),
            ];
        case DeliveryStatusEnum.OnRequest:
            return [
                'warning',
                'app.deliveryStatus.onRequest',
                variant === 'contained' ? (
                    <ExclamationMarkOutlinedIcon fontSize="inherit" strokeWidth={2} />
                ) : (
                    <ExclamationMarkIcon fontSize="inherit" strokeWidth={2.5} />
                ),
            ];
        case DeliveryStatusEnum.AvailableNoDeliveryDate:
            return [
                'warning',
                'app.deliveryStatus.availableNoDeliveryDate',
                variant === 'contained' ? (
                    <ExclamationMarkOutlinedIcon fontSize="inherit" strokeWidth={2} />
                ) : (
                    <ExclamationMarkIcon fontSize="inherit" strokeWidth={2.5} />
                ),
            ];
        case DeliveryStatusEnum.Unavailable:
        default:
            return ['negative', 'app.deliveryStatus.unavailable', <CloseIcon fontSize="inherit" />];
    }
};

interface StyleProps {
    variant: DeliveryStatusVariant;
    signalColor: ColorOptions;
}
const shouldForwardProp = shouldForwardPropExclude('variant', 'signalColor');

const Container = styled(Box, { shouldForwardProp })<StyleProps>(({ variant, signalColor, theme }) => ({
    ...theme.typography.caption,
    flexDirection: 'row',
    display: 'inline-flex',
    gap: theme.spacing(),
    alignItems: 'center',
    color: theme.palette.grey[800],

    ...(variant === 'contained' &&
        signalColor !== 'inherit' && {
            ...theme.typography.statusLabel,
            padding: '0 13px',
            height: 28,
            borderRadius: 14,
            color: theme.palette.common.white,
            backgroundColor: theme.palette[signalColor].main,
        }),
}));

const IconBox = styled(Box, { shouldForwardProp })<StyleProps>(({ variant, signalColor, theme }) => ({
    color: theme.palette.common.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,

    ...(variant === 'circleSmall' &&
        signalColor !== 'inherit' && {
            fontSize: 11,
            width: 15,
            height: 15,
            backgroundColor: theme.palette[signalColor].main,
            borderRadius: '50%',
        }),

    ...(variant === 'circle' &&
        signalColor !== 'inherit' && {
            fontSize: 14,
            width: 20,
            height: 20,
            backgroundColor: theme.palette[signalColor].main,
            borderRadius: '50%',
        }),
}));

export interface DeliveryStatusProps extends Omit<StackProps, 'variant'> {
    deliveryStatus: DeliveryStatusEnum;
    stockStatus?: StockStatus;
    deliveryTimeInDays?: number;
    variant?: DeliveryStatusVariant;
}

export const DeliveryStatus = (props: DeliveryStatusProps): ReactElement => {
    const { deliveryStatus, deliveryTimeInDays, variant = 'contained', stockStatus, ...stackProps } = props;
    const [color, translationId, icon] = getDeliveryStatusDisplayValues(deliveryStatus, variant, stockStatus);

    return (
        <Container variant={variant} signalColor={color} {...stackProps}>
            <IconBox variant={variant} signalColor={color}>
                {icon}
            </IconBox>
            <FormattedMessage id={translationId} values={{ days: deliveryTimeInDays }} />
        </Container>
    );
};
